/*
  Joseph James, 22 Oct 2026: collection page behaviour, one deferred asset.
  Loaded once from sections/main-collection-product-grid.liquid (next to
  Dawn's own facets.js). Styles live in assets/jj-collection-ui.css.

  Four pieces, moved here unchanged from inline <script> blocks on 20-21 Oct
  2026 (grid section and snippets/facets.liquid):
    1. "View more products"  - in-place next page (grid section).
    2. Native mobile sort    - <select> over the Sort button (facets snippet).
    3. Desktop filter fit    - chips shrink, then wrap (facets snippet).
    4. Sticky filter bar     - Filter | Sort row / chip bar pin under the header.

  Each piece keeps its window.jj*Ready flag so the one-time document and
  window listeners can never double-bind, even if this file were included
  twice. Element-level work (the grid observer, the sticky bar's container)
  is marked on the element instead, so it can be re-run cheaply when the
  theme editor re-renders the section ('shopify:section:load').

  Only change of substance: the "View more" MutationObserver now watches
  #ProductGridContainer (the element facets.js replaces on a filter or sort
  change) instead of document.body with subtree, so chat widgets, review
  badges and the like no longer wake it on every DOM change.
*/
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     1. "View more products" (21 Oct 2026). Fetches the next page through
     Shopify's section rendering (?section_id=...&page=N), moves its product
     cards into #product-grid, and swaps in the fresh status line and button.
     Prefetches the next page once the button is near the viewport.
     Delegated and observer-driven so it keeps working after facets.js
     replaces the grid on a filter or sort change. Any failure falls back to
     the real link.
     ------------------------------------------------------------------ */
  var cache = {};
  var io = null;
  var gridObserver = null;
  var observedGrid = null;

  function sectionUrl(href, sectionId) {
    var url = new URL(href, window.location.href);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }
  function fetchPage(url) {
    if (!cache[url]) {
      cache[url] = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .catch(function (err) { delete cache[url]; throw err; });
    }
    return cache[url];
  }
  function prefetch(wrap) {
    var btn = wrap.querySelector('[data-jj-load-more-button]');
    if (!btn) return;
    var run = function () { fetchPage(sectionUrl(btn.href, wrap.dataset.sectionId)).catch(function () {}); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 }); else window.setTimeout(run, 300);
  }
  function watch() {
    document.querySelectorAll('[data-jj-load-more]').forEach(function (wrap) {
      if (wrap.dataset.jjWatched === '1') return;
      wrap.dataset.jjWatched = '1';
      if (io) io.observe(wrap);
    });
  }
  /* Watch the grid's container only: facets.js sets its innerHTML on every
     filter or sort change, which is the one re-render we need to notice.
     Re-attached if the theme editor swaps the section (new container). */
  function observeGrid() {
    if (!window.MutationObserver) return;
    var container = document.getElementById('ProductGridContainer');
    if (!container || container === observedGrid) return;
    if (gridObserver) gridObserver.disconnect();
    gridObserver = new MutationObserver(watch);
    gridObserver.observe(container, { childList: true, subtree: true });
    observedGrid = container;
  }
  function initLoadMore() {
    if (!window.jjLoadMoreReady) {
      window.jjLoadMoreReady = true;
      io = ('IntersectionObserver' in window)
        ? new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) { io.unobserve(entry.target); prefetch(entry.target); }
            });
          }, { rootMargin: '600px 0px' })
        : null;
      document.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-jj-load-more-button]');
        if (!btn) return;
        var wrap = btn.closest('[data-jj-load-more]');
        var grid = document.getElementById('product-grid');
        if (!wrap || !grid) return;
        event.preventDefault();
        if (wrap.dataset.loading === '1') return;
        wrap.dataset.loading = '1';
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        fetchPage(sectionUrl(btn.href, wrap.dataset.sectionId)).then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.getElementById('product-grid');
          var newWrap = doc.querySelector('[data-jj-load-more]');
          if (!newGrid) { window.location.href = btn.href; return; }
          var items = Array.prototype.slice.call(newGrid.children);
          items.forEach(function (li) {
            li.classList.remove('scroll-trigger', 'animate--slide-in');
            li.removeAttribute('data-cascade');
            li.style.removeProperty('--animation-order');
            grid.appendChild(li);
          });
          if (newWrap) { wrap.replaceWith(newWrap); } else { wrap.remove(); }
          watch();
          var firstLink = items.length ? items[0].querySelector('a[href]') : null;
          if (firstLink) { firstLink.setAttribute('tabindex', '-1'); firstLink.focus({ preventScroll: true }); }
          document.dispatchEvent(new CustomEvent('jj:products-appended', { detail: { count: items.length } }));
        }).catch(function () {
          window.location.href = btn.href;
        });
      });
    }
    watch();
    observeGrid();
  }

  /* ------------------------------------------------------------------
     2. Native mobile sort (20 Oct 2026). A native <select> sits invisibly
     over the Sort button, so a tap opens the device's own picker and
     choosing an option re-sorts at once (facets.js renderPage with the
     current URL params plus the new sort_by, the same route the "Remove
     filter" chips use, so existing filters are kept). It deliberately does
     NOT submit the mobile form: serialising that form sends the dimension
     sliders' default ranges too, which shows stray Height/Width/Depth chips.
     ------------------------------------------------------------------ */
  function initSortNative() {
    if (!window.jjSortNativeReady) {
      window.jjSortNativeReady = true;
      document.addEventListener('change', function (event) {
        var select = event.target.closest('[data-jj-sort-native]');
        if (!select) return;
        var value = select.value;
        var drawerSelect = document.getElementById('SortBy-mobile');
        if (drawerSelect) drawerSelect.value = value;
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', value);
        params.delete('page');
        if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
          FacetFiltersForm.renderPage(params.toString());
        } else {
          window.location.search = params.toString();
        }
      });
    }
  }

  /* ------------------------------------------------------------------
     3. Desktop filter bar fit rule (21 Oct 2026). The chips shrink
     (assets/jj-collection-ui.css) when the screen cannot fit them all at the
     chosen width. If shrinking would cut any label off ("Co..."), the bar
     switches to wrapping instead: facets__form gets .jj-facets--wrap, chips
     go back to their natural width and flow onto a second row. Re-checked on
     resize and whenever facets.js re-renders the filters.
     ------------------------------------------------------------------ */
  var fitTimer = null;
  var fitObserver = null;
  var observedFacets = null;

  function fitCheck() {
    var forms = document.querySelectorAll('.facets-container .facets__form');
    forms.forEach(function (form) {
      form.classList.remove('jj-facets--wrap');
      if (window.innerWidth < 1012) return;
      var labels = form.querySelectorAll('.facets__summary-label');
      var clipped = Array.prototype.some.call(labels, function (label) {
        return label.offsetWidth > 0 && label.scrollWidth > label.clientWidth + 1;
      });
      if (clipped) form.classList.add('jj-facets--wrap');
    });
  }
  function fitSchedule() {
    window.clearTimeout(fitTimer);
    fitTimer = window.setTimeout(fitCheck, 60);
  }
  function observeFacets() {
    var container = document.querySelector('.facets-container');
    if (!container || !window.MutationObserver || container === observedFacets) return;
    if (fitObserver) fitObserver.disconnect();
    fitObserver = new MutationObserver(fitSchedule);
    fitObserver.observe(container, { childList: true, subtree: true });
    observedFacets = container;
  }
  function initFacetFit() {
    if (!window.jjFacetFitReady) {
      window.jjFacetFitReady = true;
      window.addEventListener('resize', fitSchedule);
      window.addEventListener('load', fitCheck);
      document.addEventListener('shopify:section:load', fitSchedule);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitCheck);
    }
    fitCheck();
    observeFacets();
  }

  /* ------------------------------------------------------------------
     4. Sticky filter bar (21 Oct 2026, Furniture Village style).
     Phones/tablets: when the Filter | Sort buttons scroll under the sticky
     header, .facets-container gets .jj-stuck and the row is pinned just
     below the header. Desktop: the same for the chip bar (.jj-stuck-desktop;
     the grey count band is hidden while pinned). Styles are in
     assets/jj-collection-ui.css; a spacer holds each bar's place in the
     page. Bars unpin when scrolled back into view or when the products end.
     ------------------------------------------------------------------ */
  var stickyUpdaters = [];
  var stickyTicking = false;

  function headerBottom(header) {
    if (!header) return 0;
    var pos = getComputedStyle(header).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    return Math.max(0, header.getBoundingClientRect().bottom);
  }

  /* One pinned bar per layout: the Filter | Sort buttons on phones and
     tablets, the chip bar on desktop. Each gets a spacer that holds its
     place in the page while it is pinned. */
  function makeSticky(container, header, bar, stuckClass, isActive) {
    if (!bar) return null;
    var spacer = document.createElement('div');
    spacer.className = 'jj-bar-spacer jj-bar-spacer--' + stuckClass;
    bar.insertAdjacentElement('afterend', spacer);
    var stuck = false;
    function stick() {
      container.style.setProperty('--jj-bar-h', bar.offsetHeight + 'px');
      container.classList.add(stuckClass);
      stuck = true;
    }
    function unstick() {
      container.classList.remove(stuckClass);
      bar.style.top = '';
      stuck = false;
    }
    return function update() {
      if (!isActive() || (!stuck && !bar.offsetParent)) {
        if (stuck) unstick();
        return;
      }
      var hb = headerBottom(header);
      var naturalTop = stuck ? spacer.getBoundingClientRect().top : bar.getBoundingClientRect().top;
      var grid = document.getElementById('product-grid');
      var pastProducts = grid ? grid.getBoundingClientRect().bottom < hb + bar.offsetHeight : false;
      if (naturalTop < hb && !pastProducts) {
        if (!stuck) stick();
        bar.style.top = hb + 'px';
      } else if (stuck) {
        unstick();
      }
    };
  }
  function stickyRun() {
    stickyTicking = false;
    stickyUpdaters.forEach(function (u) { u(); });
  }
  function stickySchedule() {
    if (stickyTicking) return;
    stickyTicking = true;
    window.requestAnimationFrame(stickyRun);
  }
  /* Binds one .facets-container (marked so a re-run after a section
     re-render only picks up a new container, never the same one twice). */
  function bindStickyContainer() {
    var container = document.querySelector('.facets-container');
    if (!container || container.dataset.jjSticky === '1') return;
    container.dataset.jjSticky = '1';
    var header = document.querySelector('.section-header.shopify-section');

    var updaters = [
      makeSticky(container, header, container.querySelector(':scope > .mobile-facets__wrapper'), 'jj-stuck', function () { return window.innerWidth < 1012; }),
      makeSticky(container, header, container.querySelector(':scope > facet-filters-form.facets'), 'jj-stuck-desktop', function () { return window.innerWidth >= 1012; })
    ].filter(Boolean);
    if (!updaters.length) return;
    stickyUpdaters = updaters;

    /* While the mobile filter drawer is open, lift the pinned bar above the
       site header: a fixed bar with a z-index is a stacking context, so the
       drawer inside it could not rise above the sticky header otherwise and
       the header covered the drawer's top (bug found on phones 21 Oct 2026). */
    var disclosure = container.querySelector('.mobile-facets__disclosure');
    if (disclosure) {
      var syncDrawer = function () { container.classList.toggle('jj-drawer-open', !!disclosure.open); };
      disclosure.addEventListener('toggle', syncDrawer);
      syncDrawer();
    }
  }
  function initStickyBar() {
    bindStickyContainer();
    if (!stickyUpdaters.length) return;
    if (!window.jjStickyBarReady) {
      window.jjStickyBarReady = true;
      window.addEventListener('scroll', stickySchedule, { passive: true });
      window.addEventListener('resize', stickySchedule);
      document.addEventListener('shopify:section:load', stickySchedule);
    }
    stickyRun();
  }

  /* ------------------------------------------------------------------
     Boot. Deferred scripts run after the document is parsed, so the
     elements exist; DOMContentLoaded is the belt and braces. The theme
     editor's 'shopify:section:load' re-runs only the cheap, idempotent
     element-level parts (observers, marks); listeners are bound once.
     ------------------------------------------------------------------ */
  function init() {
    initLoadMore();
    initSortNative();
    initFacetFit();
    initStickyBar();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () {
    watch();
    observeGrid();
    observeFacets();
    initStickyBar();
  });
})();
